import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { del, patch, post } from "common/helpers/HTTP";
import type { BaseTemplate, CreatedTemplate, Template } from "common/types";

type TemplateUpdates = Partial<BaseTemplate>;

async function createTemplateRequest(
  workspaceId: string,
  template: Template,
): Promise<CreatedTemplate> {
  return (await post(`workspaces/${workspaceId}/templates`, template)).template;
}

async function updateTemplateRequest(
  workspaceId: string,
  templateId: string,
  updates: TemplateUpdates,
) {
  await patch(`workspaces/${workspaceId}/templates/${templateId}`, updates);
}

async function deleteTemplateRequest(workspaceId: string, templateId: string) {
  await del(`workspaces/${workspaceId}/templates/${templateId}`);
}

export default function useTemplateMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { templatesKeys } = getQueryKeys(workspaceId);

  const { mutateAsync: createTemplate, isPending: isCreatingTemplate } =
    useMutation({
      mutationFn: ({ template }: { template: Template }) =>
        createTemplateRequest(workspaceId, template),
      onError: () => toast.error("Failed to create template"),
      onSuccess: (template) => {
        toast.success("Template created");

        queryClient.setQueryData(
          templatesKeys.list,
          (prevTemplates: Template[]) => [template, ...prevTemplates],
        );
      },
    });

  const { mutateAsync: updateTemplate, isPending: isUpdatingTemplate } =
    useMutation({
      mutationFn: ({
        templateId,
        updates,
      }: {
        templateId: string;
        updates: TemplateUpdates;
      }) => updateTemplateRequest(workspaceId, templateId, updates),
      onError: () => toast.error("Failed to update template"),
      onSuccess: (_, { templateId, updates }) => {
        toast.success("Template updated");

        queryClient.setQueryData(
          templatesKeys.list,
          (prevTemplates: CreatedTemplate[]) => {
            const clonedTemplates = [...prevTemplates];
            const index = clonedTemplates.findIndex(
              ({ id }) => id === templateId,
            );
            clonedTemplates[index] = {
              ...clonedTemplates[index],
              ...updates,
            };
            return clonedTemplates;
          },
        );
      },
    });

  const { mutateAsync: deleteTemplate, isPending: isDeletingTemplate } =
    useMutation({
      mutationFn: ({ templateId }: { templateId: string }) =>
        deleteTemplateRequest(workspaceId, templateId),
      onError: () => toast.error("Failed to delete template"),
      onSuccess: (_, { templateId }) => {
        toast.success("Template deleted");

        queryClient.setQueryData(
          templatesKeys.list,
          (prevTemplates: CreatedTemplate[]) =>
            prevTemplates.filter(({ id }) => id !== templateId),
        );
      },
    });

  return {
    updateTemplate,
    isUpdatingTemplate,
    createTemplate,
    isCreatingTemplate,
    deleteTemplate,
    isDeletingTemplate,
  };
}
